import { clsx } from 'clsx';
import type { FactBoxTotalsProps } from './MasterDocument.types';

/**
 * Widget de totales del documento para el panel FactBox.
 *
 * Renderiza un contenedor azul unificado con el total general, una grilla 2×2
 * de KPIs secundarios y un desglose detallado — todo dentro del mismo bloque.
 * Todos los valores monetarios son cadenas ya formateadas por el consumidor.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - FactBoxTotalsProps
 *
 * @example
 * ```tsx
 * <FactBoxTotals
 *   grandTotal={{ value: '$1.500.000', label: 'Total documento' }}
 *   kpis={[{ label: 'Subtotal', value: '$1.200.000' }]}
 *   breakdown={[{ label: 'IVA 19%', value: '$228.000' }]}
 * />
 * ```
 */
export function FactBoxTotals({
  grandTotal,
  kpis,
  breakdown,
  className,
}: FactBoxTotalsProps) {
  const hasSections = kpis.length > 0 || breakdown.length > 0;

  return (
    <div
      className={clsx(
        'bg-primary-custom-100 dark:bg-primary-custom-600/10 rounded-[10px] px-4 py-3.5',
        className,
      )}
    >
      {/* ── Grand Total ─────────────────────────────── */}
      <div className="text-xl font-extrabold tabular-nums text-primary-custom-700 dark:text-primary-custom-400 leading-tight">
        {grandTotal.value}
      </div>
      <div className="text-xs font-semibold uppercase tracking-wide text-primary-custom-600 dark:text-primary-custom-500 mt-1">
        {grandTotal.label}
      </div>
      {grandTotal.subtitle && (
        <div className="text-xs text-primary-custom-600 dark:text-primary-custom-500 mt-0.5">
          {grandTotal.subtitle}
        </div>
      )}

      {hasSections && (
        <div className="h-px bg-primary-custom-300 dark:bg-primary-custom-600/40 my-2.5" />
      )}

      {/* ── KPI grid 2×2 ────────────────────────────── */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="flex flex-col gap-0.5">
              <span className="text-xs text-primary-custom-600 dark:text-primary-custom-500 leading-tight">
                {kpi.label}
              </span>
              <span
                className={clsx(
                  'text-xs font-bold tabular-nums leading-tight',
                  kpi.color ? kpi.color : 'text-primary-custom-700 dark:text-primary-custom-400',
                )}
              >
                {kpi.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Breakdown rows ───────────────────────────── */}
      {breakdown.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-0">
          {breakdown.map((item) => (
            <div key={item.label}>
              {item.isSummary && (
                <div className="h-px bg-primary-custom-300 dark:bg-primary-custom-600/40 my-2" />
              )}
              <div className="flex items-center justify-between py-[3px]">
                <span
                  className={clsx(
                    item.isSummary
                      ? 'text-xs font-bold text-primary-custom-700 dark:text-primary-custom-400'
                      : 'text-xs text-primary-custom-600 dark:text-primary-custom-500',
                  )}
                >
                  {item.label}
                </span>
                <span
                  className={clsx(
                    'tabular-nums',
                    item.isSummary
                      ? 'text-sm font-extrabold text-primary-custom-700 dark:text-primary-custom-400'
                      : 'text-xs font-semibold',
                    item.color
                      ? item.color
                      : 'text-primary-custom-700 dark:text-primary-custom-400',
                  )}
                >
                  {item.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
